import Trans from 'next-translate/Trans';
import useLocale from 'next-translate/useTranslation';

export default function AgentPersonas() {
  const { t } = useLocale('chatbots');

  const personas = [
    {
      role: t('Support agent'),
      description: t(
        'Resolves questions from your docs and policies with accurate, on-brand answers — around the clock.',
      ),
      question: t('Do you ship to Singapore?'),
      answer: t(
        'We do! Free shipping on orders over $50, arriving in 3–5 days.',
      ),
      accent: 'bg-primary-600',
    },
    {
      role: t('Sales agent'),
      description: t(
        'Answers product questions, handles objections, and guides visitors toward the right plan.',
      ),
      question: t('Is the Pro plan worth it for a team of 4?'),
      answer: t('Yes — Pro adds 5 seats, and yearly billing saves you 20%.'),
      accent: 'bg-emerald-600',
    },
    {
      role: t('Lead generation agent'),
      description: t(
        'Captures name, email, and intent mid-conversation, then hands warm leads to your team.',
      ),
      question: t('Can I get a demo for my company?'),
      answer: t("Happy to set that up! What's the best email to reach you?"),
      accent: 'bg-amber-600',
    },
  ];

  return (
    <section className='bg-white py-24 dark:bg-slate-900 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='max-w-3xl'>
          <h2 className='text-sm font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400'>
            {t('One agent, many jobs')}
          </h2>
          <p className='mt-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            <Trans
              i18nKey='personas-headline'
              ns='chatbots'
              components={[
                <span
                  key='0'
                  className='font-serif font-medium italic text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </p>
          <p className='mt-4 max-w-2xl text-lg leading-8 text-gray-600 dark:text-slate-300'>
            {t(
              'Give your agent a role and guardrails. It answers from your data — and only your data — so every reply stays accurate and on-brand.',
            )}
          </p>
        </div>

        <div className='mt-16 grid grid-cols-1 gap-6 md:grid-cols-3'>
          {personas.map((persona, i) => (
            <div
              key={persona.role}
              className={`flex flex-col rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-900/5 dark:bg-slate-800/60 dark:ring-white/10 ${
                i === 1 ? 'md:-translate-y-4' : ''
              }`}
            >
              <div className='flex items-center gap-2.5'>
                <span
                  className={`inline-flex h-2.5 w-2.5 rounded-full ${persona.accent}`}
                ></span>
                <h3 className='text-base font-bold text-gray-900 dark:text-white'>
                  {persona.role}
                </h3>
              </div>

              <div className='mt-6 flex-1 space-y-3'>
                <div className='flex justify-end'>
                  <p className='max-w-[85%] rounded-2xl rounded-br-md bg-white px-4 py-2.5 text-sm leading-relaxed text-gray-900 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-700 dark:text-slate-100 dark:ring-0'>
                    {persona.question}
                  </p>
                </div>
                <div className='flex'>
                  <p
                    className={`max-w-[85%] rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed text-white ${persona.accent}`}
                  >
                    {persona.answer}
                  </p>
                </div>
              </div>

              <p className='mt-6 border-t border-gray-200 pt-4 text-sm leading-relaxed text-gray-600 dark:border-white/5 dark:text-slate-400'>
                {persona.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
